import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, Table, Tag, Button, Progress, Tooltip, Modal, Descriptions, message, Spin, Empty, Badge, Alert, Popconfirm, Checkbox } from "antd";
import { 
    ReloadOutlined,
    DownloadOutlined,
    FileZipOutlined,
    FilePdfOutlined,
    EyeOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SyncOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";
import { 
    getGenerations, 
    getGenerationDetails,
    getGeneratedFiles,
    downloadGeneratedFile,
    downloadCombinedPdf,
    downloadGenerationZip,
    rebuildGenerationZip,
    rebuildCombinedPdf,
    cleanupStuckGenerations,
    deleteGeneration,
} from "../../core/api/pdfParser";
import useGlobalFilters from "../../hooks/useGlobalFilters";

function GenerationHistory() {
    const [searchParams, setSearchParams] = useSearchParams();
    const generationIdFromUrl = searchParams.get("generationId");
    const { brandIds } = useGlobalFilters();
    const currentBrandId = brandIds.length > 0 ? brandIds[0] : null;
    
    const [generations, setGenerations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedGeneration, setSelectedGeneration] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [generatedFiles, setGeneratedFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);

    const deletedIdsRef = useRef(new Set());

    const openDetails = useCallback(async (generation) => {
        setSelectedGeneration(generation);
        setDetailsVisible(true);
        setFilesLoading(true);
        setGeneratedFiles([]);
        try {
            const [detailsResult, filesResult] = await Promise.all([
                getGenerationDetails(generation.id),
                getGeneratedFiles(generation.id),
            ]);
            setSelectedGeneration(detailsResult.generation || generation);
            if (filesResult.success === false) {
                message.warning("Failed to load files: " + (filesResult.error || "Unknown error"));
                setGeneratedFiles([]);
            } else {
                setGeneratedFiles(filesResult.files || []);
            }
        } catch (err) {
            message.error("Failed to load generation details: " + (err.message || "Unknown error"));
            setGeneratedFiles([]);
        } finally {
            setFilesLoading(false);
        }
    }, []);

    const fetchGenerations = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const result = await getGenerations(currentBrandId);
            const gens = (result.generations || []).filter(g => !deletedIdsRef.current.has(g.id));

            setGenerations(prev => {
                if (prev.length !== gens.length) return gens;
                const changed = gens.some((g, i) =>
                    g.id !== prev[i]?.id ||
                    g.status !== prev[i]?.status ||
                    g.processed_rows !== prev[i]?.processed_rows ||
                    g.success_count !== prev[i]?.success_count
                );
                return changed ? gens : prev;
            });
            
            if (generationIdFromUrl && generationIdFromUrl !== 'undefined' && !detailsVisible && gens.length > 0) {
                const gen = gens.find(g => {
                    const genId = g.id || g.pk;
                    const batchId = g.batch_id;
                    const urlId = parseInt(generationIdFromUrl, 10);
                    return genId === urlId || genId === generationIdFromUrl || batchId === generationIdFromUrl;
                });
                if (gen) {
                    openDetails(gen);
                    setSearchParams({}, { replace: true });
                }
            }
        } catch (err) {
            if (!silent) message.error("Failed to fetch generation history");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [generationIdFromUrl, detailsVisible, setSearchParams, openDetails, currentBrandId]);

    const pollingRef = useRef(false);
    const modalPollingRef = useRef(false);
    const generationsRef = useRef(generations);
    generationsRef.current = generations;
    const mountTimeRef = useRef(Date.now());

    const modalOpenTimeRef = useRef(0);
    useEffect(() => {
        if (!detailsVisible || !selectedGeneration?.id) return;
        modalOpenTimeRef.current = Date.now();

        const poll = async () => {
            if (modalPollingRef.current) return;
            const gen = selectedGeneration;
            const isProcessing = gen?.status === 'processing' || gen?.status === 'pending';
            const filesStillLoading = (gen?.success_count || 0) > (generatedFiles?.length || 0);
            const isRecent = Date.now() - modalOpenTimeRef.current < 30000;
            if (!isProcessing && !filesStillLoading && !isRecent) return;

            modalPollingRef.current = true;
            try {
                const [detailsResult, filesResult] = await Promise.all([
                    getGenerationDetails(selectedGeneration.id),
                    getGeneratedFiles(selectedGeneration.id),
                ]);
                const updated = detailsResult.generation || gen;
                setSelectedGeneration(prev => (prev?.id === gen.id ? updated : prev));
                if (filesResult.success !== false) {
                    setGeneratedFiles(filesResult.files || []);
                }
            } catch (_) {
                // ignore poll errors
            } finally {
                modalPollingRef.current = false;
            }
        };
        const interval = setInterval(poll, 2000);
        return () => clearInterval(interval);
    }, [detailsVisible, selectedGeneration?.id, selectedGeneration?.status, selectedGeneration?.success_count, generatedFiles?.length]);

    useEffect(() => {
        mountTimeRef.current = Date.now();
        cleanupStuckGenerations(1).catch(() => { /* ignore */ });
        fetchGenerations();

        const interval = setInterval(() => {
            if (pollingRef.current) return;
            const hasProcessing = generationsRef.current.some(g => g.status === 'processing' || g.status === 'pending');
            const isRecentMount = Date.now() - mountTimeRef.current < 30000;
            if (!hasProcessing && !isRecentMount) return;
            pollingRef.current = true;
            fetchGenerations(true).finally(() => { pollingRef.current = false; });
        }, 3000);

        return () => clearInterval(interval);
    }, [fetchGenerations]);

    const handleDownloadFile = async (fileId, filename) => {
        if (downloadingId === fileId) return; // Prevent double clicks
        setDownloadingId(fileId);
        try {
            console.log("Downloading file:", fileId, filename);
            const blob = await downloadGeneratedFile(fileId);
            if (!blob) {
                throw new Error("No file data received");
            }
            if (blob.size === 0) {
                throw new Error("File is empty");
            }
            console.log("File blob size:", blob.size);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename || `file_${fileId}.pdf`;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 100);
            message.success("File downloaded successfully");
        } catch (err) {
            console.error("Download file error:", err);
            const errorMsg = err.message || "Failed to download file";
            message.error(errorMsg);
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDownloadCombined = async (generationId) => {
        const key = `combined-${generationId}`;
        setDownloadingId(key);
        message.loading({ content: "Preparing combined PDF...", key });
        try {
            const blob = await downloadCombinedPdf(generationId, false);
            if (blob === null) {
                message.success({ content: "Download started!", key });
                return;
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `combined_${generationId}.pdf`;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
            message.success({ content: "Combined PDF downloaded", key });
        } catch (err) {
            console.error("Download combined PDF error:", err);
            const errMsg = err.message || "";
            if (errMsg.includes("not available") || errMsg.includes("rebuild") || errMsg.includes("404")) {
                message.info({ content: "Building combined PDF in background. Try again in 1-2 min.", key, duration: 5 });
                rebuildCombinedPdf(generationId).catch((e) => console.warn("Rebuild combined:", e));
            } else {
                message.error({ content: errMsg || "Failed to download combined PDF", key });
            }
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDownloadZip = async (generationId) => {
        const key = `zip-${generationId}`;
        setDownloadingId(key);
        message.loading({ content: "Preparing ZIP...", key });
        try {
            const blob = await downloadGenerationZip(generationId);
            if (blob === null) {
                message.success({ content: "Download started!", key });
                return;
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `generation_${generationId}.zip`;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
            message.success({ content: "ZIP downloaded", key });
        } catch (err) {
            console.error("Download ZIP error:", err);
            const errMsg = err.message || "";
            if (errMsg.includes("not available") || errMsg.includes("404")) {
                message.info({ content: "Building ZIP in background. Try again in 1-2 min.", key, duration: 5 });
                rebuildGenerationZip(generationId).catch((e) => console.warn("Rebuild ZIP:", e));
            } else {
                message.error({ content: errMsg || "Failed to download ZIP", key });
            }
        } finally {
            setDownloadingId(null);
        }
    };

    const handlePreviewCombined = async (generationId) => {
        const key = `preview-${generationId}`;
        setDownloadingId(`combined-${generationId}`);
        message.loading({ content: "Loading preview...", key });
        try {
            const blob = await downloadCombinedPdf(generationId, true);
            if (!blob || blob.size === 0) throw new Error("Empty response from server");
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            setPreviewVisible(true);
            message.destroy(key);
        } catch (err) {
            message.error({ content: err.message || "Failed to load preview", key });
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDeleteGeneration = async (generationId) => {
        deletedIdsRef.current.add(generationId);
        setGenerations(prev => prev.filter(g => g.id !== generationId));
        setSelectedRowKeys(prev => prev.filter(k => k !== generationId));
        try {
            const result = await deleteGeneration(generationId);
            if (result.success) {
                message.success("Generation deleted");
            } else {
                message.warning(result.message || "Server delete may have failed, removed locally");
            }
        } catch {
            message.warning("Server delete failed, removed locally");
        }
    };

    const handleBulkDelete = async () => {
        const ids = [...selectedRowKeys];
        for (const id of ids) deletedIdsRef.current.add(id);
        setGenerations(prev => prev.filter(g => !ids.includes(g.id)));
        setSelectedRowKeys([]);
        const results = await Promise.allSettled(ids.map(id => deleteGeneration(id)));
        const succeeded = results.filter(r => r.status === "fulfilled" && r.value?.success).length;
        message.success(`${succeeded}/${ids.length} generation(s) deleted`);
    };

    const rowSelection = useMemo(() => ({
        selectedRowKeys,
        onChange: setSelectedRowKeys,
    }), [selectedRowKeys]);

    /**
     * Check if generation actually completed (even if status is incorrectly "failed")
     * This handles the case where all files were generated but status wasn't saved correctly
     */
    const isGenerationComplete = (record) => {
        // Explicit completed status
        if (record.status === "completed" || record.status === "success") return true;
        // Progress-based completion: all rows processed with success
        const total = record.total_rows || 0;
        const success = record.success_count || 0;
        if (total > 0 && success > 0 && success >= total) return true;
        // Has generated files
        if (success > 0) return true;
        return false;
    };

    const getStatusTag = (status, record = null) => {
        // Override status if it's "failed" but actually completed
        if (record && status === "failed" && isGenerationComplete(record)) {
            status = "completed";
        }
        
        const statusConfig = {
            pending: { color: "default", icon: <ClockCircleOutlined />, text: "Pending" },
            processing: { color: "processing", icon: <SyncOutlined spin />, text: "Processing" },
            completed: { color: "success", icon: <CheckCircleOutlined />, text: "Completed" },
            success: { color: "success", icon: <CheckCircleOutlined />, text: "Success" },
            failed: { color: "error", icon: <CloseCircleOutlined />, text: "Failed" },
            partial: { color: "warning", icon: <ExclamationCircleOutlined />, text: "Partial" },
        };
        
        const config = statusConfig[status] || statusConfig.pending;
        return (
            <Tag color={config.color} icon={config.icon}>
                {config.text}
            </Tag>
        );
    };

    const columns = [
        {
            title: "ID",
            key: "seq_id",
            width: 60,
            render: (_, _record, index) => (
                <Tooltip title={_record.batch_id}>
                    <span style={{ fontWeight: 600 }}>{index + 1}</span>
                </Tooltip>
            ),
        },
        {
            title: "Template",
            dataIndex: "template_name",
            key: "template_name",
            ellipsis: true,
            render: (name) => name || "Unknown",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status, record) => getStatusTag(status, record),
        },
        {
            title: "Progress",
            key: "progress",
            width: 180,
            render: (_, record) => {
                const total = record.total_rows || 0;
                const processed = record.processed_rows || 0;
                const success = record.success_count || 0;
                const errors = record.error_count || 0;
                const done = processed > 0 ? processed : success + errors;
                const isComplete = record.status === "completed";
                const percent = isComplete && total > 0 ? 100 : total > 0 ? Math.round((done / total) * 100) : 0;
                const isActive = record.status === "processing" || record.status === "pending";
                
                return (
                    <div>
                        <Progress 
                            percent={percent} 
                            size="small" 
                            status={isActive ? "active" : errors > 0 ? "exception" : undefined}
                            strokeColor={errors > 0 ? undefined : "#52c41a"}
                        />
                        <span style={{ fontSize: 11, color: "#888" }}>
                            {success}/{total} 
                            {errors > 0 && <span style={{ color: "#ff4d4f" }}> ({errors} errors)</span>}
                        </span>
                    </div>
                );
            },
        },
        {
            title: "Created",
            dataIndex: "created_at",
            key: "created_at",
            width: 160,
            render: (date) => new Date(date).toLocaleString(),
        },
        {
            title: "Actions",
            key: "actions",
            width: 220,
            render: (_, record) => {
                const canDownload = isGenerationComplete(record);
                return (
                    <div style={{ display: "flex", gap: 4 }}>
                        <Tooltip title="View Details">
                            <Button
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => openDetails(record)}
                            />
                        </Tooltip>
                        {canDownload && (
                            <>
                                <Tooltip title="Download Combined PDF">
                                    <Button
                                        size="small"
                                        icon={<FilePdfOutlined />}
                                        onClick={() => handleDownloadCombined(record.id)}
                                        loading={downloadingId === `combined-${record.id}`}
                                    />
                                </Tooltip>
                                <Tooltip title="Download ZIP">
                                    <Button
                                        size="small"
                                        icon={<FileZipOutlined />}
                                        onClick={() => handleDownloadZip(record.id)}
                                        loading={downloadingId === `zip-${record.id}`}
                                    />
                                </Tooltip>
                            </>
                        )}
                        <Popconfirm title="Delete this generation?" onConfirm={() => handleDeleteGeneration(record.id)} okText="Yes" cancelText="No">
                            <Tooltip title="Delete">
                                <Button size="small" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    </div>
                );
            },
        },
    ];

    const fileColumns = [
        {
            title: "Filename",
            dataIndex: "filename",
            key: "filename",
            ellipsis: true,
        },
        {
            title: "Row",
            dataIndex: "row_id",
            key: "row_id",
            width: 80,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status) => getStatusTag(status),
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_, record) => {
                const isDisabled = record.status !== "success";
                return (
                    <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) {
                                handleDownloadFile(record.id, record.filename);
                            }
                        }}
                        loading={downloadingId === record.id}
                        disabled={isDisabled}
                    >
                        Download
                    </Button>
                );
            },
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h5 style={{ margin: 0, fontWeight: 600 }}>
                    Generation History
                    <Badge 
                        count={generations.filter(g => g.status === "processing").length} 
                        style={{ marginLeft: 12, backgroundColor: "#1890ff" }}
                        title="Processing jobs"
                    />
                </h5>
                <div style={{ display: "flex", gap: 8 }}>
                    {selectedRowKeys.length > 0 && (
                        <Popconfirm
                            title={`Delete ${selectedRowKeys.length} generation(s)?`}
                            onConfirm={handleBulkDelete}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button danger icon={<DeleteOutlined />}>
                                Delete Selected ({selectedRowKeys.length})
                            </Button>
                        </Popconfirm>
                    )}
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchGenerations}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            {loading && generations.length === 0 ? (
                <div style={{ textAlign: "center", padding: 60 }}>
                    <Spin size="large" />
                </div>
            ) : generations.length === 0 ? (
                <Card>
                    <Empty 
                        description="No generation history yet"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                </Card>
            ) : (
                <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
                    <Table
                        dataSource={generations}
                        columns={columns}
                        rowKey="id"
                        rowSelection={rowSelection}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `${total} generations`,
                        }}
                    />
                </Card>
            )}

            {/* Details Modal */}
            <Modal
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            background: "#1a1a2e",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <FilePdfOutlined style={{ color: "#fff", fontSize: 20 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 600 }}>Generation Details</div>
                            <div style={{ fontSize: 12, color: "#888" }}>
                                {selectedGeneration?.batch_id?.slice(0, 8)}...
                            </div>
                        </div>
                    </div>
                }
                open={detailsVisible}
                onCancel={() => setDetailsVisible(false)}
                zIndex={100000}
                width={900}
                footer={[
                    <Button key="close" onClick={() => setDetailsVisible(false)}>
                        Close
                    </Button>,
                    selectedGeneration && isGenerationComplete(selectedGeneration) && (
                        <Button
                            key="zip"
                            icon={<FileZipOutlined />}
                            onClick={() => handleDownloadZip(selectedGeneration.id)}
                            loading={downloadingId === `zip-${selectedGeneration?.id}`}
                        >
                            Download All (ZIP)
                        </Button>
                    ),
                    selectedGeneration && isGenerationComplete(selectedGeneration) && (
                        <Button
                            key="preview-combined"
                            icon={<EyeOutlined />}
                            onClick={() => handlePreviewCombined(selectedGeneration.id)}
                            loading={downloadingId === `combined-${selectedGeneration?.id}`}
                        >
                            Preview Combined PDF
                        </Button>
                    ),
                    selectedGeneration && isGenerationComplete(selectedGeneration) && (
                        <Button
                            key="combined"
                            type="primary"
                            icon={<FilePdfOutlined />}
                            onClick={() => handleDownloadCombined(selectedGeneration.id)}
                            loading={downloadingId === `combined-${selectedGeneration?.id}`}
                            style={{ background: "#1a1a2e" }}
                        >
                            Download Combined PDF
                        </Button>
                    ),
                ].filter(Boolean)}
            >
                {selectedGeneration && (
                    <div>
                        <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="Status">
                                {getStatusTag(selectedGeneration.status)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Data Source">
                                {selectedGeneration.data_file_name || selectedGeneration.data_source || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Total Rows">
                                {selectedGeneration.total_rows ?? selectedGeneration.totalRows ?? 0}
                            </Descriptions.Item>
                            <Descriptions.Item label="Success">
                                <span style={{ color: "#52c41a" }}>
                                    {selectedGeneration.success_count ?? selectedGeneration.successCount ?? 0}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Errors">
                                <span style={{ color: (selectedGeneration.error_count ?? selectedGeneration.errorCount ?? 0) > 0 ? "#ff4d4f" : "inherit" }}>
                                    {selectedGeneration.error_count ?? selectedGeneration.errorCount ?? 0}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Created">
                                {(selectedGeneration.created_at || selectedGeneration.createdAt) ? new Date(selectedGeneration.created_at || selectedGeneration.createdAt).toLocaleString() : '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        {selectedGeneration.error_count > 0 && selectedGeneration.error_details && (
                            <Alert
                                message="Errors occurred during generation"
                                description={
                                    <pre style={{ 
                                        maxHeight: 150, 
                                        overflow: "auto", 
                                        margin: 0,
                                        fontSize: 11 
                                    }}>
                                        {JSON.stringify(selectedGeneration.error_details, null, 2)}
                                    </pre>
                                }
                                type="error"
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <h6 style={{ marginBottom: 12, fontWeight: 600 }}>
                            Generated Files
                            {generatedFiles.length > 0 && (
                                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: "#888" }}>
                                    ({generatedFiles.length} file{generatedFiles.length !== 1 ? 's' : ''})
                                </span>
                            )}
                        </h6>
                        {filesLoading ? (
                            <div style={{ textAlign: "center", padding: 40 }}>
                                <Spin />
                            </div>
                        ) : generatedFiles.length === 0 ? (
                            <Empty 
                                description="No files generated yet"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                style={{ padding: 40 }}
                            />
                        ) : (
                            <Table
                                dataSource={generatedFiles}
                                columns={fileColumns}
                                rowKey="id"
                                size="small"
                                pagination={{
                                    pageSize: 5,
                                    showSizeChanger: false,
                                }}
                            />
                        )}
                    </div>
                )}
            </Modal>

            {/* Preview Combined PDF Modal */}
            <Modal
                title="Preview Combined PDF"
                open={previewVisible}
                onCancel={() => {
                    setPreviewVisible(false);
                    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
                }}
                width={900}
                zIndex={100001}
                footer={<Button onClick={() => { setPreviewVisible(false); if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}>Close</Button>}
            >
                {previewUrl && (
                    <iframe
                        src={previewUrl}
                        style={{ width: "100%", height: "70vh", border: "none" }}
                        title="Combined PDF Preview"
                    />
                )}
            </Modal>
        </div>
    );
}

export default GenerationHistory;
